import { IonCard, IonCardContent, IonImg, IonText } from '@ionic/react';
import { report } from 'process';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import { ActionSheetReportCard } from '@/components/Actionsheets';
import { Icon, IconName } from '@/components/Icon';
import { IconSize } from '@/components/Icon/types';
import { Tag } from '@/components/Tag/Tag';
import { toast } from '@/components/Toaster/Toast';

import { getTitleAndDescriptionFromInfo } from '@/utils/getTitleAndDescriptionFromReportData';
import { mergeClassNames } from '@/utils/mergeClassNames';

import { useReportContext } from '@/contexts/reportContext';

import ReportCardPattern from '@/assets/report-card-pattern.svg';

import { ReportInfo, ReportStatus, ReportType } from '@/models';

import styles from './SessionCard.module.css';

import type { ITag } from '@/validations/tag';

interface ReportCardProps {
  clientId: string;
  id: string;
  name: string;
  date: Date;
  reportType: ReportType;
  reportInfo: ReportInfo;
  tags: ITag[];
}

export const SessionReportCard = ({ clientId, id, name, date, reportType, reportInfo, tags }: ReportCardProps) => {
  const history = useHistory();
  const { title, description } = getTitleAndDescriptionFromInfo(name, date);
  console.log('reportInfo in SessionCard:', reportInfo);

  const backTarget = `/clients/${clientId}`;
  const pending = reportInfo.status === ReportStatus.PENDING;
  const error =
    reportInfo.status === ReportStatus.ERROR ||
    reportInfo.reportType === ReportType.NOTENOUGHDATA ||
    reportInfo.reportType === ReportType.ERROR ||
    reportInfo.reportType === ReportType.KNEEL ||
    reportInfo.reportType === ReportType.KNEER;

  const { doDelete } = useReportContext();
  const [showActionSheet, setShowActionSheet] = useState(false);

  const iconMap: Map<ReportType, IconName> = new Map([
    [ReportType.WALK, 'walk'],
    [ReportType.RUN, 'run'],
    [ReportType.SQUAT, 'squat'],
    [ReportType.SLSQUAT, 'singleLegSquat'],
  ]);

  const activityNamesMap = new Map([
    [ReportType.WALK, 'Walk'],
    [ReportType.RUN, 'Run'],
    [ReportType.SQUAT, 'Squat'],
    [ReportType.SLSQUAT, 'Single Leg Squat'],
  ]);

  const routeSegmentMap = new Map([
    [ReportType.WALK, 'Walk'],
    [ReportType.RUN, 'Run'],
    [ReportType.SQUAT, 'Squat'],
    [ReportType.SLSQUAT, 'SingleLegSquat'],
  ]);

  const handleClick = () => {
    if (routeSegmentMap.has(reportType)) {
      history.push(`/reportsV2/${routeSegmentMap.get(reportType)}/${id}`, {
        title,
        description,
        backTarget,
      });
    }
  };

  // Get the session index from the reportInfo.reportName (#number at the end)
  const movementCount = useMemo(() => {
    const match = reportInfo.reportName.match(/#(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  }, [reportInfo.filename]);

  return (
    <>
      <ActionSheetReportCard
        doDelete={doDelete}
        isError={error}
        isOpen={showActionSheet}
        onClose={() => setShowActionSheet(false)}
        reportInfo={reportInfo}
        showDeleteError={() =>
          toast({
            variant: 'error',
            title: 'Error deleting file',
            description: `No report ID for the given report ${reportInfo.filename}.`,
          })
        }
        showPDFError={() =>
          toast({
            variant: 'error',
            title: 'Error downloading file',
            description: `Can't download PDF for ${reportInfo.filename}, please contact support.`,
          })
        }
        // onGeneratePDF={handleGeneratePDF}
      />
      <IonCard
        className={mergeClassNames(styles.card, error && styles.error, pending && styles.pending)}
        onClick={!error && !pending ? handleClick : undefined}>
        <div className={styles.left}>
          <Icon className={styles.icon} color='currentColor' name={iconMap.get(reportType) ?? 'walk'} />
          <IonImg className={styles.pattern} src={ReportCardPattern} />
        </div>

        <IonCardContent className={styles.content}>
          <div className={styles.leftContent}>
            {error && (
              <>
                <div className={styles.errorContent}>
                  <Icon name='error' />
                  <p>
                    {reportInfo.status === ReportStatus.ERROR && `An issue occurred while processing this recording`}
                    {reportInfo.reportType === ReportType.ERROR &&
                      `Sorry, we couldn't process your data. Please contact support.`}
                    {reportInfo.reportType === ReportType.KNEEL && `Left Thigh and Calf Sensors seem to be swapped.`}
                    {reportInfo.reportType === ReportType.KNEER && `Right Thigh and Calf Sensors seem to be swapped.`}
                    {reportInfo.reportType === ReportType.NOTENOUGHDATA && `Not enough data to generate report.`}
                  </p>
                </div>
              </>
            )}

            {pending && (
              <div className={styles.pendingContent}>
                <Icon className={styles.spinnerIcon} name='spinner' />
                <p>Recording is being processed</p>
              </div>
            )}

            {!pending && !error && (
              <>
                <IonText className={styles.name}>
                  {activityNamesMap.get(reportType)}{' '}
                  {movementCount != null && <span className={styles.movementCount}>#{movementCount}</span>}
                </IonText>

                <div className={styles.tags}>
                  {tags.at(0)?.label != '' && (
                    <>
                      {tags.map((tag) => (
                        <Tag key={`tag-${tag.id}`} variant='gray'>
                          {' '}
                          {tag.label}{' '}
                        </Tag>
                      ))}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          <div className={styles.rightContent}>
            <IonText class={styles.date}>
              {date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </IonText>
            <button
              className={styles.menuButton}
              onClick={(event) => {
                event.stopPropagation();
                setShowActionSheet(true);
              }}>
              <Icon className={styles.iconMenu} color='#829696' name='menu' size={IconSize.small} />
            </button>
          </div>
        </IonCardContent>
      </IonCard>
    </>
  );
};
